import * as React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  TextField,
  Box,
  CircularProgress,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { useCallback, useEffect, useState } from "react";
import { message } from "antd";
import { API_BASE_URL, getAuthHeaders } from "../API/Api";

const QUOTE_STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "in_review", label: "In Review" },
  { value: "quoted", label: "Quoted" },
  { value: "accepted", label: "Accepted" },
  { value: "rejected", label: "Rejected" },
  { value: "cancelled", label: "Cancelled" },
];

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const formatPhone = (countryCode, phone) => {
  const parts = [countryCode, phone].filter(Boolean);
  return parts.join(" ").trim();
};

const getApiErrorMessage = (errorData, fallback) => {
  if (errorData?.message) return errorData.message;
  const errors = errorData?.errors;
  if (errors && typeof errors === "object") {
    const first = Object.values(errors).flat().find(Boolean);
    if (first) return String(first);
  }
  return fallback;
};

const QuoteStatus = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [linePricing, setLinePricing] = useState({});
  const [updateBody, setUpdateBody] = useState("");
  const [quoteUpdates, setQuoteUpdates] = useState([]);

  const fetchQuote = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/quotes/${id}`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
      const responseData = await res.json();
      const quoteData = responseData?.data ?? responseData;
      setQuote(quoteData);
      setSelectedStatus(quoteData.status || "");

      const lines = Array.isArray(quoteData.lines) ? quoteData.lines : [];
      const pricing = lines.reduce((acc, line) => {
        if (line.id) {
          acc[line.id] = {
            unit_price: line.unit_price != null ? String(line.unit_price) : "",
          };
        }
        return acc;
      }, {});
      setLinePricing(pricing);
    } catch (error) {
      console.error("Error fetching quote:", error);
      message.error("Failed to load quote");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchQuote();
  }, [fetchQuote]);

  const handleSubmit = async () => {
    const lines = Array.isArray(quote?.lines) ? quote.lines : [];

    const linesPayload = lines
      .filter((line) => line.id)
      .map((line) => {
        const pricing = linePricing[line.id] || {};
        const payload = { id: line.id };
        if (pricing.unit_price !== "") {
          payload.unit_price = Number(pricing.unit_price);
        }
        return payload;
      })
      .filter(
        (line) => line.unit_price != null && !Number.isNaN(line.unit_price),
      );

    setSaving(true);
    try {
      const body = { status: selectedStatus };
      if (updateBody.trim()) {
        body.body = updateBody.trim();
      }
      if (linesPayload.length > 0) {
        body.lines = linesPayload;
      }

      const res = await fetch(`${API_BASE_URL}/admin/quotes/${id}/updates`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });

      const responseData = await res.json().catch(() => ({}));

      if (!res.ok) {
        const validationError = responseData?.errors?.status?.[0];
        throw new Error(
          validationError ||
            getApiErrorMessage(
              responseData,
              `HTTP error! Status: ${res.status}`,
            ),
        );
      }

      const update = responseData?.data ?? {};
      if (update.id || update.body) {
        setQuoteUpdates((prev) => [update, ...prev]);
      }
      setUpdateBody("");

      setQuote((prev) =>
        prev
          ? {
              ...prev,
              ...(responseData?.data ?? {}),
              status: selectedStatus,
            }
          : prev,
      );
      message.success("Quote submitted successfully");
      navigate("/admin/quotes");
    } catch (error) {
      console.error("Error submitting quote:", error);
      message.error(error.message || "Failed to submit quote");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <CircularProgress sx={{ color: "#3A6F43" }} />
      </Box>
    );
  }

  if (!quote) {
    return (
      <Box sx={{ margin: "25px", textAlign: "center", py: 6 }}>
        <Typography variant="h6" color="text.secondary">
          Quote not found
        </Typography>
        <Button
          sx={{ mt: 2 }}
          variant="outlined"
          onClick={() => navigate("/admin/quotes")}
        >
          Back to Quotes
        </Button>
      </Box>
    );
  }

  const user = quote.user || {};
  const lines = Array.isArray(quote.lines) ? quote.lines : [];

  const infoFields = [
    { label: "Reference", value: quote.reference },
    { label: "Name", value: user.name || quote.name },
    { label: "Email", value: user.email || quote.email },
    { label: "Company", value: user.company || quote.company },
    { label: "County", value: user.county || quote.county },
    {
      label: "Phone",
      value: formatPhone(
        user.country_code || quote.country_code,
        user.phone || quote.phone,
      ),
    },
    { label: "Submitted", value: formatDate(quote.created_at) },
    { label: "Customer Note", value: quote.customer_note },
  ].filter((f) => f.value != null && f.value !== "");

  return (
    <div style={{ margin: "25px", marginBottom: "100px", overflow: "hidden" }}>
      <Box
        sx={{
          padding: "16px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "#3A6F43",
          borderRadius: "8px 8px 0 0",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/admin/quotes")}
            sx={{
              color: "#fff",
              textTransform: "none",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
            }}
          >
            Back
          </Button>
          <Typography variant="h6" sx={{ color: "#fff", fontWeight: 600 }}>
            Quote Status & Pricing
          </Typography>
        </Box>
        {quote.reference && (
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
            {quote.reference}
          </Typography>
        )}
      </Box>

      <Paper sx={{ borderRadius: "0 0 8px 8px", p: 2.5 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, color: "#555" }}>
          Quote Information
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 1,
            mb: 2,
          }}
        >
          {infoFields.map((field) => (
            <Box key={field.label}>
              <Typography variant="caption" color="text.secondary">
                {field.label}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {field.value}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            borderTop: "1px solid #e0e0e0",
            pt: 2,
            mb: 2,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, color: "#555" }}>
            Update Status
          </Typography>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="status-label">Status</InputLabel>
            <Select
              labelId="status-label"
              label="Status"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              {QUOTE_STATUSES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedStatus !== quote.status && (
            <Typography
              variant="body2"
              sx={{ mt: 1, color: "#e65100", fontWeight: 500 }}
            >
              {quote.status} → {selectedStatus}
            </Typography>
          )}
        </Box>

        {lines.length > 0 && (
          <Box sx={{ borderTop: "1px solid #e0e0e0", pt: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: "#555" }}>
              Products & Unit Prices
            </Typography>
            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "2fr 0.8fr 1fr",
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  backgroundColor: "#f5f5f5",
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                <Typography variant="caption" fontWeight={600}>
                  Product
                </Typography>
                <Typography variant="caption" fontWeight={600}>
                  Qty
                </Typography>
                <Typography variant="caption" fontWeight={600}>
                  Unit Price (£)
                </Typography>
              </Box>
              {lines.map((line, idx) => (
                <Box
                  key={line.id || idx}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "2fr 0.8fr 1fr",
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    alignItems: "center",
                    borderBottom:
                      idx < lines.length - 1 ? "1px solid #e0e0e0" : "none",
                    backgroundColor: idx % 2 === 0 ? "#fafafa" : "#fff",
                  }}
                >
                  <Typography variant="body2">
                    {line.product_name || "—"}
                  </Typography>
                  <Typography variant="body2">
                    {line.quantity ?? "—"}
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    placeholder="0.00"
                    value={linePricing[line.id]?.unit_price ?? ""}
                    onChange={(e) =>
                      setLinePricing((prev) => ({
                        ...prev,
                        [line.id]: {
                          ...prev[line.id],
                          unit_price: e.target.value,
                        },
                      }))
                    }
                    inputProps={{ min: 0, step: "0.01" }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Box sx={{ borderTop: "1px solid #e0e0e0", pt: 2, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, color: "#555" }}>
            Send Update to Customer
          </Typography>

          {quoteUpdates.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1, display: "block" }}
              >
                Sent Updates
              </Typography>
              {quoteUpdates.map((update) => (
                <Box
                  key={update.id}
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    p: 1.5,
                    mb: 1,
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(update.created_at)}
                    {update.admin?.name ? ` · ${update.admin.name}` : ""}
                    {update.type ? ` · ${update.type.replace(/_/g, " ")}` : ""}
                  </Typography>
                  {update.body && (
                    <Typography
                      variant="body2"
                      sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
                    >
                      {update.body}
                    </Typography>
                  )}
                  {Array.isArray(update.files) &&
                    update.files.map((file) => (
                      <Typography
                        key={file.id}
                        variant="body2"
                        sx={{ mt: 0.5 }}
                      >
                        <a
                          href={file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {file.name}
                        </a>
                      </Typography>
                    ))}
                </Box>
              ))}
            </Box>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Message"
              placeholder="Your quote is ready. Please review the pricing below."
              value={updateBody}
              onChange={(e) => setUpdateBody(e.target.value)}
            />
          </Box>
        </Box>

        <Box
          sx={{
            borderTop: "1px solid #e0e0e0",
            pt: 2,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => navigate("/admin/quotes")}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={handleSubmit}
            sx={{
              backgroundColor: "#3A6F43",
              textTransform: "none",
              px: 4,
              "&:hover": { backgroundColor: "#2f5a36" },
            }}
          >
            {saving ? "Submitting..." : "Submit"}
          </Button>
        </Box>
      </Paper>
    </div>
  );
};

export default QuoteStatus;
